import React, { createContext, useContext, useMemo, useState } from 'react';

type LeftSidebarState = {
	content: React.ReactNode | null;
	owner?: string;
};

export type LeftSidebarController = {
	content: React.ReactNode | null;
	setContent: (content: React.ReactNode | null, owner?: string) => void;
	clear: (owner?: string) => void;
};

const LeftSidebarContext = createContext<LeftSidebarController | undefined>(undefined);

export const LeftSidebarProvider = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	const [state, setState] = useState<LeftSidebarState>({ content: null });

	const controller = useMemo<LeftSidebarController>(
		() => ({
			content: state.content,
			setContent: (content: React.ReactNode | null, owner?: string) => {
				setState({ content, owner });
			},
			clear: (owner?: string) => {
				setState((s) => {
					if (owner && s.owner && s.owner !== owner) {
						return s;
					}
					return { content: null, owner: undefined };
				});
			},
		}),
		[state.content],
	);

	return <LeftSidebarContext.Provider value={controller}>{children}</LeftSidebarContext.Provider>;
};

export const useLeftSidebarController = (): LeftSidebarController => {
	const ctx = useContext(LeftSidebarContext);
	if (!ctx) {
		throw new Error('useLeftSidebarController must be used within LeftSidebarProvider');
	}
	return ctx;
};
